import type { TFunction } from "i18next";
import type { ConstraintViolationApiDto, ErrorApiDto } from "@/types/api";

/**
 * Turns backend validation failures into guest-facing sentences.
 *
 * The backend stopped rendering error text (techbase 1.1.0): it sends a stable `code` plus
 * structured `args`, and the client owns the wording. That is what lets a German guest read a
 * German error without the server knowing anything about locale.
 *
 * Everything here is tolerant of the pre-1.1.0 shape, because this app deploys BEFORE the backend
 * does — see docs/locale-removal-plan.md, R1. Until that deploy lands, responses carry only an
 * English `message` and `resolve*` falls back to the generic copy rather than showing it raw.
 */

/** i18n namespace holding one entry per backend code. */
const NS = "errors.backend";

/** Fallback shown when a code has no translation yet — never the server's English string. */
const GENERIC_KEY = "errors.somethingWentWrong";

/**
 * Short, sentence-safe names for the three document slots.
 *
 * The upload-zone titles cannot be reused here: the Spanish front-ID title is "Cédula / Licencia
 * de Conducir / Pasaporte", the back-ID one is written in the second person about the reader's own
 * document, and the front title is swapped out entirely under a listing's ID-type restriction. A
 * sentence naming what ANOTHER guest still owes needs a stable noun, so it gets its own key.
 */
const DOC_NAME_KEY_BY_FILE: Readonly<Record<string, string>> = Object.freeze({
  ID_FRONT: "form.idVerification.docName.idFront",
  ID_BACK: "form.idVerification.docName.idBack",
  SELFIE: "form.idVerification.docName.selfie",
});

/** The translated short name for a `GuestPictureType` wire value, or the raw value if unknown. */
export function documentLabel(file: unknown, t: TFunction): string {
  if (typeof file !== "string") return "";
  const key = DOC_NAME_KEY_BY_FILE[file];
  return key ? t(key, { defaultValue: file }) : file;
}

export function getViolations(error: unknown): ConstraintViolationApiDto[] {
  const data = extractEnvelope(error);
  const constraints = data?.constraints;
  return Array.isArray(constraints) ? constraints.filter((v) => v && typeof v.code === "string") : [];
}

/** The server's top-level discriminator, e.g. `VALIDATION_ERROR`. Undefined on pre-1.1.0. */
export function getErrorCode(error: unknown): string | undefined {
  const data = extractEnvelope(error);
  return typeof data?.errorCode === "string" && data.errorCode ? data.errorCode : undefined;
}

/**
 * The i18n key for a violation.
 *
 * Structural failures collapse: `validation.required` + `args.field` becomes one sentence template
 * per constraint type ("{{field}} is required") plus a translated field name, rather than needing a
 * distinct key for every field of every form.
 */
export function violationI18nKey(violation: ConstraintViolationApiDto): string {
  return `${NS}.${violation.code}`;
}

/**
 * Interpolation params for a violation, with `field` replaced by its *translated* name so the
 * sentence template can embed it directly.
 */
export function violationI18nArgs(
  violation: ConstraintViolationApiDto,
  t: TFunction,
): Record<string, unknown> {
  const args = { ...(violation.args ?? {}) };
  // `guestId` is the guest's DOCUMENT NUMBER — a passport or national-ID number. Dropping it here
  // is what makes "never interpolated" a property of the code rather than a bet that no locale
  // string ever grows a `{{guestId}}` placeholder. Callers that need it to LOOK UP a guest read
  // `violation.args` directly; nothing that renders gets to see it.
  delete args.guestId;
  if (typeof args.field === "string") {
    args.field = t(`errors.fields.${args.field}`, { defaultValue: args.field });
  }
  // `file` is a GuestPictureType enum name. Left raw, every surface that renders a violation shows
  // the guest the literal string "ID_FRONT".
  if (typeof args.file === "string") {
    args.file = documentLabel(args.file, t);
  }
  return args;
}

/** Render one violation, or undefined when this app has no copy for that code. */
export function resolveViolation(
  violation: ConstraintViolationApiDto,
  t: TFunction,
): string | undefined {
  const key = violationI18nKey(violation);
  const rendered = t(key, { ...violationI18nArgs(violation, t), defaultValue: "" });
  return rendered || undefined;
}

/**
 * Every violation on an error that this app can render, in server order.
 *
 * Empty when the response predates 1.1.0, carries no constraints, or uses codes this build does not
 * know — callers should fall back to {@link resolveErrorMessage}.
 */
export function resolveViolations(error: unknown, t: TFunction): string[] {
  return getViolations(error)
    .map((v) => resolveViolation(v, t))
    .filter((m): m is string => Boolean(m));
}

/**
 * The single best guest-facing sentence for an error.
 *
 * Order: first renderable violation → generic copy. The server's `message` is deliberately NOT a
 * fallback: it is English-only and developer-facing, so showing it to a guest is worse than showing
 * translated generic copy.
 */
export function resolveErrorMessage(error: unknown, t: TFunction): string {
  const [first] = resolveViolations(error, t);
  if (first) return first;
  // Guaranteed non-empty: callers gate a visible alert on this being truthy, so an empty or
  // undefined result (an i18next instance that has not finished initialising) would swallow the
  // failure entirely and leave the guest looking at a button that did nothing.
  const generic = t(GENERIC_KEY);
  return typeof generic === "string" && generic !== "" ? generic : GENERIC_KEY;
}

/**
 * What may be written to the console for a failed API call.
 *
 * `args` is never included. `guest.file.missing` carries `args.guestId`, which is the guest's
 * DOCUMENT NUMBER — a national ID or passport number — and the whole response body used to be
 * logged verbatim on every API error. Codes, the HTTP status and the server's discriminator are
 * everything a developer needs to identify the failure; none of them is personal data.
 */
export function redactedErrorForLog(error: unknown): {
  status?: number;
  errorCode?: string;
  codes: string[];
} {
  const data = extractEnvelope(error);
  const status = typeof data?.code === "number" ? data.code : undefined;
  const out: { status?: number; errorCode?: string; codes: string[] } = {
    codes: getViolations(error).map((v) => v.code),
  };
  if (status !== undefined) out.status = status;
  const errorCode = getErrorCode(error);
  if (errorCode) out.errorCode = errorCode;
  return out;
}

/**
 * `redactedErrorForLog` plus the diagnostics that exist only when the rejection is a real `Error`.
 *
 * The API layer never rejects with one: `ApiClient.formatApiError` returns an `ErrorApiDto`, a
 * plain object, for both HTTP failures and transport failures. So `error instanceof Error` inside
 * a store or hook catch means the fault was thrown by OUR code — a TypeError, a bad read, an
 * aborted navigation — and it arrives with no `constraints`, no `errorCode` and no numeric `code`.
 * Redacted alone, every one of those logs as the single word `{codes: []}`, which names neither
 * what failed nor where.
 *
 * Including `message` and `stack` for that case leaks nothing: an `Error` here was constructed by
 * this codebase, not filled in from a response body, so it cannot be carrying `args.guestId`. The
 * envelope case is untouched and still prints codes and status only.
 *
 * One object rather than a second `console.error` argument, so an API failure — which has no
 * stack by construction — does not print a trailing bare `undefined` on every log line.
 */
export function errorLogDetail(error: unknown): Record<string, unknown> {
  const detail: Record<string, unknown> = { ...redactedErrorForLog(error) };
  if (error instanceof Error) {
    detail.thrown = error.message;
    if (error.stack) detail.stack = error.stack;
  }
  return detail;
}

function extractEnvelope(error: unknown): Partial<ErrorApiDto> | undefined {
  if (!error || typeof error !== "object") return undefined;
  const candidate = error as { response?: { data?: unknown } } & Partial<ErrorApiDto>;
  // Accept both a raw axios error and the envelope `formatApiError` already unwrapped.
  const data = candidate.response?.data;
  if (data && typeof data === "object") return data as Partial<ErrorApiDto>;
  return candidate;
}
